import { useDispatch, useSelector } from 'react-redux'
import { getActiveAttendant, getAllAttendants } from '../../selectors/attendant'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { transferTabMutation } from '../Apis'
import {
  clearOrderInProgress,
  clearSelectedOrder,
  orderDetailsRequested,
  saveOrder,
  updateOrderInProgress,
} from '../../actions/order'
import { ToastManager } from '../../utils/toastManager'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { sendCFDScreenNav } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../../VNCustomerFacingDisplay/Reducer'
import { useCallback, useMemo } from 'react'
import useSpinner from '../../context/hooks/useSpinner'

export const useTransferTab = () => {
  const dispatch = useDispatch()
  const allAttendants = useSelector(getAllAttendants)
  const networkAvailable = getNetworkAvailableValue()
  const { startSpinner, stopSpinner } = useSpinner()
  const cfdMode = useSelector(getDeviceMode) === MODES.POS
  const activeAttendant = useSelector(getActiveAttendant)
  const allAttendantNames = useMemo(() => {
    return Object.values(allAttendants).map((attendant) => {
      return { name: attendant.name, id: attendant.uuid }
    })
  }, [allAttendants])

  const searchNames = useCallback(
    (query) => {
      const queryParts = query?.toLowerCase().split(' ')

      return allAttendantNames.filter((attendant) => {
        const nameParts = attendant?.name?.toLowerCase().split(' ')
        return queryParts.every((qPart) =>
          nameParts?.some((nPart) => {
            return nPart?.startsWith(qPart) && attendant.id !== activeAttendant?.uuid
          }),
        )
      })
    },
    [allAttendantNames, activeAttendant],
  )

  /**
   * This function will handle both offline and online tab transfers.
   */
  const transferTab = useCallback(
    async (tabOrder, toEmployeeId, toEmployeeName, approverId = null, onClose = () => {}) => {
      if (tabOrder.wasCreatedInStadium && !networkAvailable) return

      startSpinner()

      if (networkAvailable) {
        try {
          const result = await transferTabMutation(approverId, toEmployeeId, tabOrder.employeeId, tabOrder.uuid)

          if (!result) {
            ToastManager.error('Failed to transfer order. Please try again.', { autoClose: false })
            stopSpinner()
            onClose()
            return
          }
        } catch (error) {
          ToastManager.error('Failed to transfer order. Please try again.', { autoClose: false })
          stopSpinner()
          onClose()
          return
        }
      }

      dispatch(
        saveOrder({
          uuid: tabOrder.uuid,
          employeeId: toEmployeeId,
        }),
      )

      dispatch(
        updateOrderInProgress({
          employeeId: toEmployeeId,
        }),
      )

      // closes the transfer tab modal
      onClose()

      // closes the order details screen
      dispatch(clearSelectedOrder())
      dispatch(clearOrderInProgress())

      if (networkAvailable) {
        dispatch(orderDetailsRequested(tabOrder.uuid))
      }

      ToastManager.success(`Order ${tabOrder.orderNumber} transferred successfully to ${toEmployeeName}`)

      stopSpinner()
      if (cfdMode) {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
      }
    },
    [dispatch, networkAvailable, startSpinner, stopSpinner, cfdMode],
  )

  return {
    searchNames,
    transferTab,
  }
}
